import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './styles.css';

export default function Services() {
  const services = [
    {
      id: 'setup',
      icon: 'fa-server',
      title: 'System Setup',
      description: 'Windows installation, Office products, driver updates, and complete system optimization for peak performance.',
      path: '/services/setup',
      category: 'IT Support',
      features: ['Windows Installation', 'Office Setup', 'Driver Updates']
    },
    {
      id: 'troubleshooting',
      icon: 'fa-tools',
      title: 'Tech Support',
      description: 'Troubleshooting, virus removal, performance tuning, and hardware diagnostics for all your devices.',
      path: '/services/troubleshooting',
      category: 'IT Support',
      features: ['Virus Removal', 'Hardware Diagnostics', 'Performance Tuning']
    },
    {
      id: 'development',
      icon: 'fa-code',
      title: 'Web & App Development',
      description: 'React, Express, PostgreSQL, and full-stack development with modern frameworks and best practices.',
      path: '/services/development',
      category: 'Development',
      features: ['React', 'Node.js', 'PostgreSQL', 'Full-Stack']
    },
    {
      id: 'automation',
      icon: 'fa-robot',
      title: 'Automation',
      description: 'PowerShell scripting, modular scaffolding, and task automation to streamline your business operations.',
      path: '/services/automation',
      category: 'Automation',
      features: ['PowerShell', 'Task Automation', 'Scaffolding']
    },
    {
      id: 'consulting',
      icon: 'fa-brain',
      title: 'Consulting',
      description: 'Architecture planning, deployment strategy, role-based access, and digital transformation guidance.',
      path: '/services/consulting',
      category: 'Consulting',
      features: ['Architecture Planning', 'Strategy', 'Digital Transformation']
    },
    {
      id: 'networking',
      icon: 'fa-network-wired',
      title: 'Networking Solutions',
      description: 'LAN/WAN setup, router configuration, and network security to keep your business connected.',
      path: '/services/networking',
      category: 'Networking',
      features: ['LAN/WAN', 'Router Setup', 'Network Security']
    },
    {
      id: 'security',
      icon: 'fa-shield-alt',
      title: 'Network Security',
      description: 'Firewall setup, VPN configuration, network monitoring, and security audits to protect your data.',
      path: '/services/security',
      category: 'Security',
      features: ['Firewall', 'VPN', 'Network Monitoring']
    },
    {
      id: 'wireless',
      icon: 'fa-wifi',
      title: 'Wireless Networking',
      description: 'Wi-Fi setup, mesh networks, signal optimization, and wireless infrastructure planning.',
      path: '/services/wireless',
      category: 'Networking',
      features: ['Wi-Fi Setup', 'Mesh Networks', 'Signal Optimization']
    }
  ];

  // Structured Data for Services
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "IT Services | Raffcodes - Technology Solutions",
    "description": "Comprehensive IT services including system setup, web development, networking, automation, and consulting in Nairobi, Kenya.",
    "url": "https://raffcodes.tech/services",
    "about": {
      "@type": "Organization",
      "name": "Raffcodes",
      "description": "IT solutions provider in Nairobi, Kenya"
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": services.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Service",
          "name": service.title,
          "description": service.description,
          "url": `https://raffcodes.tech${service.path}`,
          "serviceType": service.category
        }
      }))
    }
  };

  // Group services by category
  const categories = [...new Set(services.map(s => s.category))];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>IT Services | Raffcodes - Technology Solutions in Nairobi</title>
        <meta name="description" content="Explore Raffcodes' comprehensive IT services including system setup, web development, networking, automation, and consulting. Professional technology solutions in Nairobi, Kenya." />
        <meta name="keywords" content="IT services Nairobi, system setup, web development Kenya, networking solutions, automation, IT consulting, tech support, Raffcodes" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/services" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="IT Services | Raffcodes - Technology Solutions in Nairobi" />
        <meta property="og:description" content="Comprehensive IT services including system setup, web development, networking, automation, and consulting in Nairobi, Kenya." />
        <meta property="og:url" content="https://raffcodes.tech/services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/services-og.jpg" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="IT Services | Raffcodes - Technology Solutions" />
        <meta name="twitter:description" content="Comprehensive IT services in Nairobi including system setup, web development, networking, and consulting." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/services-og.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page services-page">
        {/* Header Section */}
        <section className="services-header">
          <div className="container">
            <div className="services-header-content">
              <span className="header-tag">What We Do</span>
              <h1>Our Services</h1>
              <p className="header-subtitle">
                Comprehensive IT solutions tailored to your business needs. 
                From system setup to full-stack development, we've got you covered.
              </p>
              <div className="header-stats">
                <div className="stat-item">
                  <span className="stat-number">{services.length}+</span>
                  <span className="stat-label">Services</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">{categories.length}</span>
                  <span className="stat-label">Categories</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">24/7</span>
                  <span className="stat-label">Support</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="services-grid-section">
          <div className="container">
            <div className="services-grid">
              {services.map((service) => (
                <Link 
                  key={service.id} 
                  to={service.path} 
                  className="service-card-link"
                >
                  <div className="service-card-clickable">
                    <div className="service-icon-wrapper">
                      <i className={`fas ${service.icon}`}></i>
                    </div>
                    <div className="service-category-tag">{service.category}</div>
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                    <div className="service-features-preview">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="feature-preview-tag">{feature}</span>
                      ))}
                    </div>
                    <div className="service-arrow">
                      <span>Learn More</span>
                      <i className="fas fa-arrow-right"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="why-choose-section">
          <div className="container">
            <div className="section-header">
              <span className="section-tag">Why Raffcodes</span>
              <h2>Why Choose Us?</h2>
              <p className="section-subtitle">We combine technical expertise with a commitment to excellence</p>
            </div>
            <div className="why-grid">
              <div className="why-card">
                <div className="why-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <h4>Quick Response</h4>
                <p>We respond to all inquiries within hours, not days</p>
              </div>
              <div className="why-card">
                <div className="why-icon">
                  <i className="fas fa-user-tie"></i>
                </div>
                <h4>Expert Team</h4>
                <p>Skilled professionals with years of industry experience</p>
              </div>
              <div className="why-card">
                <div className="why-icon">
                  <i className="fas fa-hand-holding-usd"></i>
                </div>
                <h4>Competitive Pricing</h4>
                <p>Quality services at fair, transparent rates</p>
              </div>
              <div className="why-card">
                <div className="why-icon">
                  <i className="fas fa-headset"></i>
                </div>
                <h4>24/7 Support</h4>
                <p>Round-the-clock support for your peace of mind</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-cta">
          <div className="container">
            <div className="cta-content">
              <div className="cta-text">
                <h2>Need a Custom Solution?</h2>
                <p>Let's discuss your specific requirements and build something amazing together.</p>
              </div>
              <div className="cta-actions">
                <a href="/contact" className="cta primary">
                  <i className="fas fa-paper-plane"></i> Contact Us
                </a>
                <a href="/portfolio" className="cta secondary">
                  <i className="fas fa-th-list"></i> View Portfolio
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}